// WHAT IS THIS BOARD? — the bench's answer, from three sources that can disagree.
//
// BLE Test used to decide the type from the device's own INFO reply alone
// (`identity.deviceId`), so a Curtain Controller showed no curtain buttons: the
// INFO line names a type the panel had no branch for, or names nothing at all,
// and the panel fell back to relay tiles or drew nothing (owner, 2026-10-25:
// "DeviceTypeID 13 Curtain Controller … there actually should come curtain
// buttons which are not in ble test").
//
// The server knows. `GetDeviceDetailbyUniqueNumber` answers for ANY unique
// number regardless of ownership — D4F98D14B194 comes back DeviceID 13,
// "Curtain Controller", unassigned — and one call costs nothing next to a BLE
// handshake. So:
//
//   1. OVERRIDE   what the operator picked by hand. Always wins: it is the
//                 escape hatch for a board whose record is wrong.
//   2. SERVER     DevicesInstalled.DeviceID. The registration decides what the
//                 product IS, and it is what every other screen in the app
//                 draws from, so the bench matching it is the whole point.
//   3. DEVICE     the INFO reply. The only source for a board that has no
//                 database record at all — a fresh piece off the line.
//
// ⚠️ A DISAGREEMENT IS A FINDING, NOT A TIE TO BREAK QUIETLY. If the server says
// 13 and the firmware says 49, one of them is wrong about a deployed product —
// which is exactly the kind of thing a bring-up screen exists to surface. The
// chosen type wins, and `disagree` makes the screen say so.
//
// Pure. The I/O (one fetch on connect) stays in the screen.

const asId = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
};

export const SOURCE_OVERRIDE = 'override';
export const SOURCE_SERVER = 'server';
export const SOURCE_DEVICE = 'device';

export const SOURCE_LABEL = {
  [SOURCE_OVERRIDE]: 'picked by hand',
  [SOURCE_SERVER]: 'from the server',
  [SOURCE_DEVICE]: 'the device said so',
};

/**
 * -> { deviceId, source, disagree } — `deviceId` null when nothing knows.
 * `disagree` is set only when the server and the device both answered and
 * differ, whichever of them won.
 */
export function pickBenchType({ override = null, serverDeviceId = null, deviceDeviceId = null } = {}) {
  const o = asId(override);
  const s = asId(serverDeviceId);
  const d = asId(deviceDeviceId);
  const disagree = s != null && d != null && s !== d;
  if (o != null) return { deviceId: o, source: SOURCE_OVERRIDE, disagree };
  if (s != null) return { deviceId: s, source: SOURCE_SERVER, disagree };
  if (d != null) return { deviceId: d, source: SOURCE_DEVICE, disagree };
  return { deviceId: null, source: null, disagree: false };
}

/** The line the ⋮ menu and the log show. '' when nothing is known yet. */
export function benchTypeNote(pick, { typeName = '' } = {}) {
  if (!pick || pick.deviceId == null) return '';
  const who = SOURCE_LABEL[pick.source] || '';
  const name = String(typeName || '').trim();
  const head = `${pick.deviceId}${name ? ` ${name}` : ''}${who ? ` (${who})` : ''}`;
  return pick.disagree
    ? `${head} — ⚠️ the server and the device disagree about this type`
    : head;
}

const benchType = { pickBenchType, benchTypeNote, SOURCE_LABEL };
export default benchType;
